import React, { createContext, useContext, useId } from 'react';
import { geographicConflicts, geographicReviewCurrent, type GeographicScope, type GeographicReview } from '../../functions/src/common/geographicConflict';
import { geographicScope } from '../../functions/src/common/geographicConflict';
import './geographicConflict.css';

const FieldContext = createContext<{ fields: Set<string>; countryFields: string[]; noteId: string } | null>(null);

export function useGeographicField(name?: string, country = false) {
    const context = useContext(FieldContext);
    const key = name || (country && context?.countryFields.length === 1 ? context.countryFields[0] : '');
    return context && key && context.fields.has(key)
        ? { 'data-geographic-conflict': true, 'aria-describedby': context.noteId } : {};
}

export function GeographicInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
    const warning = useGeographicField(props.name);
    return <input {...props} {...warning} />;
}

export function GeographicTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
    const warning = useGeographicField(props.name);
    return <textarea {...props} {...warning} />;
}

export function GeographicConflictNotice({ scope, consultant = false, review, id }: {
    scope: GeographicScope; consultant?: boolean; review?: GeographicReview | null; id?: string;
}) {
    const conflicts = geographicConflicts(scope);
    if (!conflicts.length)
        return null;
    const reviewed = geographicReviewCurrent(scope, review);
    return <div id={id} role={consultant ? 'alert' : 'status'} aria-live="polite" className={`geographic-notice ${consultant && !reviewed ? 'geographic-alert' : ''}`}>
        {conflicts.map((conflict, index) => <p key={index}>{consultant
            ? <><strong>🚩 Geographic Conflict Detected:</strong> Target Country ({conflict.countries.join(', ')}) does not match Asset Location ({conflict.detected.join(', ')}).</>
            : <>⚠️ Notice: Your selected Country of Concern ({conflict.countries.join(', ')}) differs from the location detected in your assets description ({conflict.detected.join(', ')}). Please ensure the selected country matches the assets you want assessed.</>}</p>)}
        {consultant && reviewed && <p>Target confirmed by {review!.confirmedByName} · {review!.confirmedAt}</p>}
    </div>;
}

export function GeographicFields({ code, intake, children }: { code: string; intake: any; children: React.ReactNode }) {
    const noteId = useId();
    const scope = geographicScope(code, intake);
    const conflicts = geographicConflicts(scope);
    const context = { fields: new Set(conflicts.flatMap(conflict => conflict.fields)),
        countryFields: scope.groups.flatMap(group => group.countryFields), noteId };
    return <FieldContext.Provider value={context}><GeographicConflictNotice scope={scope} id={noteId} />{children}</FieldContext.Provider>;
}

export function GeographicReviewPanel({ scope, confirmed, onConfirm, disabled = false }: {
    scope: GeographicScope; confirmed: boolean; onConfirm: (value: boolean) => void; disabled?: boolean;
}) {
    if (!geographicConflicts(scope).length)
        return null;
    return <section className="space-y-3"><GeographicConflictNotice scope={scope} consultant />
        <label className="geographic-confirm"><input type="checkbox" checked={confirmed} disabled={disabled}
            onChange={event => onConfirm(event.target.checked)} />
            I confirm {scope.groups.flatMap(group => group.countries).filter((country, index, countries) => countries.indexOf(country) === index).join(', ')} as the primary target. Other locations are context.</label>
        <p className="text-sm text-slate-600 dark:text-slate-300">Correct the country or asset fields if needed. Changes require confirmation again.</p>
    </section>;
}
